/**
 * InfinitSales - WhatsApp Business Chat Window Component
 * Padronizado com WhatsApp Pessoal + AI Workspace
 * 
 * Diferenças de estrutura de dados (mantidas internamente):
 * - chat.id instead of chat.remoteJid
 * - msg.wamid instead of msg.key.id
 * - msg.from_me instead of msg.key?.fromMe
 * - msg.text instead of msg.message?.conversation
 * - msg.media_url, msg.media_type for media
 * - msg.timestamp (ISO string) instead of messageTimestamp
 * - Handles 24h window error (específico do Business)
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Send,
  Mic,
  MoreVertical,
  Bell,
  User,
  UserCheck,
  Loader2,
  ArrowDown,
  Check,
  CheckCheck,
  Clock,
  Zap,
  AlertCircle,
  X,
  Bot,  ArrowLeft,
  BanIcon,
  Tags,
  Tag,
  MessageSquarePlus,
  FileText,
  History,
  Settings,
  Forward,
  Smartphone,
} from 'lucide-react';
import messageAuthor from '../../utils/messageAuthor';
import WhoAnswersBadge from '../../components/inbox/WhoAnswersBadge';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDayLabel } from '../../components/chat/ChatDateDivider';
import { statusInfo } from '../../components/chat/MessageStatus';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
// Frente C4 (flag chat_cache): conversa ja vista pinta na hora.
import useFeatureFlags, { getFeatureFlag } from '../../hooks/useFeatureFlags';
import useAgentAnswers from '../../hooks/useAgentAnswers';
import { getChatEntry, setChatEntry, setChatMessages } from '../../services/inbox/chatCache';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useAuth } from '../../contexts/AuthContext';
import renderVariablesInText from '../../utils/messageVariables';
import { getDelegation } from '../../utils/delegation';
import CreateNotificationModal from '../../components/notifications/CreateNotificationModal';
import HandoffBanner from '../../components/HandoffBanner';
import waBusinessService from '../../services/wa-business/waBusinessService';
import WABusinessMessageMedia from '../../components/WABusinessMedia';

// AI Workspace imports
import {
  AIWorkspace,
  MessageCheckbox,
  MessageRolePill,
  AttachedFilesPreview,
  useAIWorkspace,
  getWorkspaceColors,
  BLOCKED_MESSAGE_TYPES,
  MAX_SELECTED_MESSAGES,
  ACTION_TYPES,
  extractMessagesPayload,
} from '../../components/AIWorkspace';
import aiAgentService from '../../services/global/aiAgentService';
import fileUploadService from '../../services/global/fileUploadService';
import useAgentCheck from '../../components/AIWorkspace/hooks/useAgentCheck';
import ScheduleMessageModal from '../../components/followups/ScheduleMessageModal';
import FollowupBadge from '../../components/followups/FollowupBadge';
import LabelPickerModal from '../../components/labels/LabelPickerModal';
import TransferContactModal from '../../components/contacts/TransferContactModal';
import ContactFeedbackModal from '../../components/contacts/ContactFeedbackModal';
import ContactDetailsDrawer from '../../components/contacts/ContactDetailsDrawer';
import ContactChannelChips from '../../components/contacts/ContactChannelChips';
import ErrorBoundary, { DrawerErrorFallback } from '../../components/ErrorBoundary';
import RequiredFieldsSignal from '../../components/contacts/RequiredFieldsSignal';
import RequiredFieldsPrompt from '../../components/contacts/RequiredFieldsPrompt';
import ContactTagsManagerModal from '../../components/contacts/ContactTagsManagerModal';
import MessageText from '../../components/MessageText';
import RecordingBar from '../../components/RecordingBar';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import AttachButton from '../../components/chat/AttachButton';
import SavedMessagesDrawer from '../../components/messages/SavedMessagesDrawer';
import ForwardModal from '../../components/messages/ForwardModal';
// Ações de mensagem (flag message_actions): responder citando, reagir,
// encaminhar pelo menu da bolha, barra de seleção. Compartilhado pelos shells.
import {
  MessageActionMenu,
  ReactionChips,
  QuotedMessage,
  ReplyPreview,
  SelectionBar,
  messageActionCaps,
  whyCannotEdit,
  quotedFromMessage,
} from '../../components/messages';
import WorkspaceConfigDrawer from '../../components/AIWorkspace/WorkspaceConfigDrawer';
import useWorkspaceConfig from '../../components/AIWorkspace/hooks/useWorkspaceConfig';
import useContactLabelsCount from '../../hooks/useContactLabelsCount';
import useContactAssignments from '../../hooks/useContactAssignments';
import useGestorInternal from '../../hooks/useGestorInternal';
import useTransferRoulettes from '../../hooks/useTransferRoulettes';
import useOriginNumber from '../../hooks/useOriginNumber';
import useThreadEvents from '../../hooks/useThreadEvents';
import ThreadEventCard from '../../components/chat/ThreadEventCard';
import ToolUsageCard from '../../components/chat/ToolUsageCard';
import AdContextCard, { adContextOf } from '../../components/chat/AdContextCard';
import { displayText } from '../../utils/messagePlaceholder';
import MessagesSkeleton from '../../components/inbox/MessagesSkeleton';
import { brandName } from '../../components/brand/InboxBrand';

// ============================================
// UTILITY FUNCTIONS (outside component for reuse)
// ============================================

/**
 * Robustly parse timestamp from various formats
 * Handles: ISO strings, Unix timestamps (seconds/milliseconds), Date objects
 */
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // Already a valid Date object
  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? null : timestamp;
  }
  
  // Number: Unix timestamp (seconds or milliseconds)
  if (typeof timestamp === 'number') {
    // If timestamp is in seconds (< year 3000 in seconds), convert to ms
    // Unix timestamp in seconds: 1706097600 (10 digits)
    // Unix timestamp in ms: 1706097600000 (13 digits)
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // String: try multiple parsing strategies
  if (typeof timestamp === 'string') {
    // Try direct ISO parse first
    let date = new Date(timestamp);
    if (!isNaN(date.getTime())) return date;
    
    // Try parsing as numeric string (Unix timestamp)
    const numericValue = Number(timestamp);
    if (!isNaN(numericValue)) {
      const ms = numericValue < 10000000000 ? numericValue * 1000 : numericValue;
      date = new Date(ms);
      if (!isNaN(date.getTime())) return date;
    }
    
    // Try common date formats that might fail in some browsers
    // Format: "2024-01-24 12:00:00" (without T)
    const withT = timestamp.replace(' ', 'T');
    date = new Date(withT);
    if (!isNaN(date.getTime())) return date;
  }
  
  return null;
};

/**
 * Get timestamp value for sorting (returns ms or 0)
 */
const getTimestampMs = (timestamp) => {
  const date = parseTimestamp(timestamp);
  return date ? date.getTime() : 0;
};

const ChatWindow = ({ chat, chats = [], onMessageSent, autopilotContacts, autopilotMode, blockedContacts, activeAgentName, channelPaused = false, onToggleAutopilot, onToggleBlockedContact, isHandoff = false, handoffMetadata = null, onResumeAgent, onChatPromoted, onContactNameUpdated, onContactDeleted, isMobile = false, onBack }) => {
  const { colors, theme } = useTheme();
  const { createNotification } = useNotifications();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  // Paginacao por scroll up: comeca com PAGE_SIZE mensagens (mais rapido que
  // 100) e carrega mais 30 ao scrollar para o topo. hasMoreMessages controla
  // se ainda ha o que buscar; loadingMoreMessages evita disparar varios
  // fetches concorrentes.
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const PAGE_SIZE = 30;
  const SCROLL_TOP_THRESHOLD = 80;
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [windowError, setWindowError] = useState(null); // 24h window error
  const janelaSelo = useMemo(() => {
    const li = chat?.last_inbound_at ? new Date(chat.last_inbound_at) : null;
    if (!li || Number.isNaN(li.getTime())) {
      return chat ? { aberta: false, texto: 'Janela de 24 h fechada: o contato ainda não escreveu. Envie um template da Meta.' } : null;
    }
    const fim = new Date(li.getTime() + 24 * 60 * 60 * 1000);
    if (fim.getTime() <= Date.now()) {
      return { aberta: false, texto: 'Janela de 24 h fechada: envie um template da Meta ou aguarde o contato escrever.' };
    }
    const hoje = fim.toDateString() === new Date().toDateString();
    const hora = fim.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return { aberta: true, texto: `Janela de 24 h aberta até ${hoje ? hora : `${fim.toLocaleDateString('pt-BR')} ${hora}`}` };
  }, [chat?.last_inbound_at, chat]);
  const [regeneratingId, setRegeneratingId] = useState(null);
  
  // Attached files state (from AI Workspace)
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSavedDrawer, setShowSavedDrawer] = useState(false);
  // Qual aba do SavedMessagesDrawer abre por default ('saved' | 'templates').
  // Os atalhos de "fora da janela 24h" setam 'templates' porque template e a
  // unica opcao valida nesse cenario.
  const [savedDrawerInitialTab, setSavedDrawerInitialTab] = useState('saved');
  const [showWorkspaceConfig, setShowWorkspaceConfig] = useState(false);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTagsManager, setShowTagsManager] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [showCreateNotification, setShowCreateNotification] = useState(false);

  // Handoff banner dismiss (X) — espelha o wa_personal: persiste por contato +
  // motivo em localStorage; um NOVO motivo (nova transferência) volta a mostrar.
  const [handoffBannerDismissed, setHandoffBannerDismissed] = useState(false);
  const _handoffPhoneKey = chat?.contact_phone || '';
  const _handoffReason = handoffMetadata?.handoff_reason || handoffMetadata?.reason || '';
  useEffect(() => {
    try {
      const map = JSON.parse(localStorage.getItem('handoffDismissedWB') || '{}');
      setHandoffBannerDismissed(map[_handoffPhoneKey] === (_handoffReason || '__'));
    } catch { setHandoffBannerDismissed(false); }
  }, [_handoffPhoneKey, _handoffReason]);
  const dismissHandoffBanner = () => {
    setHandoffBannerDismissed(true);
    try {
      const map = JSON.parse(localStorage.getItem('handoffDismissedWB') || '{}');
      map[_handoffPhoneKey] = _handoffReason || '__';
      localStorage.setItem('handoffDismissedWB', JSON.stringify(map));
    } catch { /* localStorage indisponível — só não persiste */ }
  };

  // Encaminhar mensagens: reusa a seleção do aiWorkspace (Chamar o Agente). O
  // ícone no header abre este modal com as mensagens já selecionadas.
  const [showForwardModal, setShowForwardModal] = useState(false);

  // Ações de mensagem (MESSAGE_ACTIONS_ENABLED): com a flag desligada o shell
  // se comporta como antes; só a marca "editada" e "Mensagem apagada" valem
  // sempre (são correção de renderização, não feature).
  const { flags: featureFlags } = useFeatureFlags();
  const messageActionsOn = !!featureFlags?.message_actions;
  const msgCaps = useMemo(() => messageActionCaps('wa_business'), []);
  // Mensagem escolhida em "Responder" (normalizada): { id, externalId, text,
  // fromMe, mediaType, senderName }. null = sem citação.
  const [replyTo, setReplyTo] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // O campo cresce com o texto tambem quando o valor entra por codigo ("Usar
  // resposta" do agente, Resposta Rapida, mensagem pronta): o onChange so roda
  // ao digitar, e um setTimeout no handler nao cobre o campo ser montado de
  // novo (o AI Workspace fecha e o compositor volta com 1 linha e barra de
  // rolagem). Caso do dono em 10/10/2026. O ref de callback cuida da montagem;
  // o efeito, de toda mudanca do valor.
  const ajustarAlturaDoInput = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
  }, []);
  const attachInputRef = useCallback((el) => {
    inputRef.current = el;
    ajustarAlturaDoInput(el);
  }, [ajustarAlturaDoInput]);
  useEffect(() => {
    ajustarAlturaDoInput(inputRef.current);
  }, [newMessage, ajustarAlturaDoInput]);
  const currentChatRef = useRef(null);

  // AI Workspace hook
  const aiWorkspace = useAIWorkspace();
  const workspaceConfigState = useWorkspaceConfig();
  const { roulettes: transferRoulettes, assignToRoulette } = useTransferRoulettes();
  const [rouletteBusyId, setRouletteBusyId] = useState(null);
  // Funcionalidade B — número de ORIGEM (por onde a mensagem sai).
  const origin = useOriginNumber(chat?.contact_phone || chat?.id);
  const contactLabels = useContactLabelsCount(chat?.contact_phone || chat?.id);
  // Responsável do contato (assigned_to). Membro humano em atendimento pausa
  // o agente para este contato (regra do agent_router no backend), mesmo com
  // o autopilot ligado — o badge e o toggle abaixo refletem isso.
  const { getAssignment } = useContactAssignments();
  const assignedTo = getAssignment(chat?.contact_phone);
  // "Responder novos contatos" só para contatos novos: o backend diz se o
  // agente atende ESTE contato (contato antigo não mostra o agente).
  const agentAnswers = useAgentAnswers(chat?.contact_phone, assignedTo?.assigned_at || 0);
  // Cards de sistema na thread (transferências) — mesma fonte do wa-personal
  // (contact_assignment_events, endpoint agnóstico de canal). Relê quando o
  // responsável muda ao vivo, para o card aparecer sem F5.
  const threadEvents = useThreadEvents(
    chat?.contact_phone, assignedTo?.assigned_at || null
  );
  const assignedMember = assignedTo?.type === 'member' ? assignedTo : null;
  // Thread interno da Ponte do Gestor: o Gestor responde ali por desenho —
  // o cabeçalho troca o "Agente pausado" por um aviso próprio.
  const gestorInternal = useGestorInternal(chat?.contact_phone);
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Gravacao de audio (voice note estilo WhatsApp)
  const audioRecorder = useAudioRecorder();
  const [sendingAudio, setSendingAudio] = useState(false);

  // Keep track of current chat for SSE callbacks
  useEffect(() => {
    currentChatRef.current = chat;
  }, [chat]);

  // Reset workspace and attached files when chat changes
  useEffect(() => {
    aiWorkspace.resetWorkspace();
    setNewMessage('');
    setAttachedFiles([]);
    setWindowError(null);
    audioRecorder.cancel();
    setShowForwardModal(false);
  }, [chat?.id]);

  const { 
    handleAgentError, 
    CreateAgentModal,
    isAgentConfigured 
  } = useAgentCheck();

  // ============================================
  // SSE EVENT HANDLERS
  // ============================================

  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    // Only process WA Business messages
    if (data.source !== 'wa_business') return;

    // Check if message is for current chat
    if (data.chat_id !== currentChat.id) return;

    console.log('[WA Business ChatWindow] SSE NEW_MESSAGE:', data);

    const newMsg = data.message;
    if (!newMsg) return;

    // Colaboração em workspace compartilhado: o backend faz broadcast de cada
    // mensagem enviada para todos os membros. Quem ENVIOU já tem o eco
    // otimista — pula o próprio (compara com a identidade real do humano, que
    // em sessão delegada é o ator). Outros membros veem aparecer com o selo
    // "Enviado por X" (msg.sent_by_name já é renderizado na bolha).
    if (newMsg.from_me && newMsg.sent_by_user_id) {
      const delegation = getDelegation();
      const myRealId = delegation?.actorUserId || user?.id;
      if (newMsg.sent_by_user_id === myRealId) return;
    }

    setMessages(prev => {
      const exists = prev.some(m => 
        m.id === newMsg.id || 
        m.wamid === newMsg.wamid
      );
      
      if (exists) return prev;
      
      const updated = [...prev, newMsg];
      setTimeout(() => scrollToBottom(), 100);
      return updated;
    });

    // Mark as read
    if (!newMsg.from_me) {
      waBusinessService.markAsRead(currentChat.id).catch(() => {});
    }
  }, []);

  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    if (data.source !== 'wa_business') return;

    console.log('[WA Business ChatWindow] SSE MESSAGE_STATUS:', data);

    setMessages(prev => prev.map(msg => {
      if (msg.wamid === data.wamid) {
        return {
          ...msg,
          status: data.status,
          error_message: data.error || null,
          // Envio recuperado (2026-09-28): a Meta recusou o envio do agente
          // mas entregou; o backend promove o doc `failed` ao wamid real e
          // avisa aqui. Sem trocar o wamid, delivered/read seguintes nao
          // casariam ate recarregar.
          ...(data.new_wamid ? { wamid: data.new_wamid } : {}),
        };
      }
      return msg;
    }));

    // Show error if 24h window
    if (data.error && data.error.includes('24h')) {
      setWindowError(data.error);
    }
  }, []);

  // Reação: NÃO é mensagem nova — carimba o emoji na bolha alvo que já está
  // na thread (antes só aparecia ao recarregar). `field` distingue a reação
  // do CLIENTE (reaction) da do OPERADOR pelo celular (reaction_from_me);
  // valor null = reação removida.
  useSSEEvent(SSE_EVENTS.MESSAGE_REACTION, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;
    if (data.source !== 'wa_business') return;
    if (data.chat_id && data.chat_id !== currentChat.id) return;

    const field = data.field === 'reaction_from_me' ? 'reaction_from_me' : 'reaction';
    setMessages(prev => prev.map(msg => (
      msg.wamid === data.wamid || (data.message_id && msg.id === data.message_id)
        ? { ...msg, [field]: data.reaction || null }
        : msg
    )));
  }, []);

  // Edição pelo cliente (Cloud API `edit`): o backend atualiza o doc
  // original e manda o texto novo; a bolha troca o texto e ganha "editada".
  useSSEEvent(SSE_EVENTS.MESSAGE_EDITED, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;
    if (data.source !== 'wa_business') return;
    if (data.chat_id && data.chat_id !== currentChat.id) return;

    setMessages(prev => prev.map(msg => (
      (data.wamid && msg.wamid === data.wamid) || (data.message_id && msg.id === data.message_id)
        ? {
            ...msg,
            ...(typeof data.text === 'string' ? { text: data.text } : {}),
            edited: true,
            edited_at: data.edited_at || msg.edited_at || new Date().toISOString(),
          }
        : msg
    )));
  }, []);

  // Apagada pelo cliente (Cloud API `revoke`): a bolha vira "Mensagem apagada".
  useSSEEvent(SSE_EVENTS.MESSAGE_DELETED, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;
    if (data.source !== 'wa_business') return;
    if (data.chat_id && data.chat_id !== currentChat.id) return;

    setMessages(prev => prev.map(msg => (
      (data.wamid && msg.wamid === data.wamid) || (data.message_id && msg.id === data.message_id)
        ? { ...msg, deleted: true, deleted_at: data.deleted_at || msg.deleted_at || new Date().toISOString() }
        : msg
    )));
  }, []);

  // ============================================
  // LOAD MESSAGES
  // ============================================

  // Ref para o AbortController do fetch atual. Quando o operador troca de
  // chat antes da resposta chegar, abortamos o anterior para nao misturar
  // mensagens de chats diferentes no setMessages (race condition).
  const messagesAbortRef = useRef(null);
  // Frente C4 (flag chat_cache): id da conversa cuja lista de mensagens em
  // estado e a desta conversa (vinda do cache ou do fetch). O espelho
  // estado->cache so grava quando bate com o chat atual.
  const cacheKeyRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!chat?.id) return;

    // Cancela qualquer fetch de mensagens ainda em voo do chat anterior.
    if (messagesAbortRef.current) {
      messagesAbortRef.current.abort();
    }
    const controller = new AbortController();
    messagesAbortRef.current = controller;
    const requestedChatId = chat.id;

    // Frente C4: conversa ja vista pinta na hora com o cache e o fetch abaixo
    // revalida por baixo (sem skeleton). Sem a flag, skeleton como hoje.
    const cacheOn = getFeatureFlag('chat_cache');
    const cached = cacheOn ? getChatEntry('wa_business', requestedChatId) : null;
    cacheKeyRef.current = null;
    if (cached && Array.isArray(cached.messages)) {
      cacheKeyRef.current = requestedChatId;
      setMessages(cached.messages);
      setLoading(false);
      setTimeout(() => scrollToBottom(), 50);
    } else {
      setLoading(true);
    }
    setWindowError(null);
    // Reset do paginador para o novo chat
    setHasMoreMessages(cached && Array.isArray(cached.messages) ? cached.messages.length >= PAGE_SIZE : true);
    setLoadingMoreMessages(false);

    // SEMPRE busca o historico (loadMessages so roda quando ha chat.id — ver o
    // useEffect). ANTES pulava quando chat._isNew, escondendo o historico de
    // contatos EXISTENTES abertos via busca/kanban (mesmo bug do wa_personal:
    // "Nenhuma mensagem ainda" num lead com conversa). Contato genuinamente novo
    // (sem chat.id) nem chega aqui.
    try {
      const response = await waBusinessService.getMessages(
        chat.id, PAGE_SIZE, 0, { signal: controller.signal }
      );
      // Guard adicional: se o usuario trocou de chat enquanto a resposta
      // ainda estava no fly (controller foi abortado tarde demais), nao
      // sobrescreve o estado do chat atual.
      if (requestedChatId !== chat?.id) return;

      let loadedMessages = response.data || [];
      // Se voltou menos que PAGE_SIZE, chegamos ao inicio do historico.
      setHasMoreMessages(loadedMessages.length >= PAGE_SIZE);

      // Sort by timestamp (oldest first) using robust parsing
      loadedMessages = [...loadedMessages].sort((a, b) => {
        const timeA = getTimestampMs(a.timestamp) || getTimestampMs(a.created_at);
        const timeB = getTimestampMs(b.timestamp) || getTimestampMs(b.created_at);
        return timeA - timeB;
      });

      if (cacheOn) {
        cacheKeyRef.current = requestedChatId;
        setChatMessages('wa_business', requestedChatId, loadedMessages);
      }
      setMessages(loadedMessages);

      // Mark as read
      waBusinessService.markAsRead(chat.id).catch(() => {});

      setTimeout(() => scrollToBottom(), 100);

    } catch (err) {
      // CanceledError do axios = troca de chat normal, nao logar como erro
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('[WA Business ChatWindow] Error loading messages:', err);
      setMessages([]);
    } finally {
      // So limpa loading se ainda for o chat atual (caso contrario o novo
      // fetch ja resetou o estado)
      if (messagesAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [chat?.id, chat?._isNew]);

  useEffect(() => {
    if (chat?.id) {
      loadMessages();
    } else {
      cacheKeyRef.current = null;
      setMessages([]);
    }
    // Cleanup: aborta o fetch em voo quando o componente desmontar ou o
    // chat trocar antes de carregar.
    return () => {
      if (messagesAbortRef.current) {
        messagesAbortRef.current.abort();
        messagesAbortRef.current = null;
      }
    };
  }, [chat?.id, loadMessages]);

  // Frente C4: espelha o estado no cache (mensagem nova via SSE, status de
  // entrega, reacao, eco do envio). Declarado DEPOIS do efeito de carga para
  // a troca de conversa zerar cacheKeyRef antes de qualquer espelho.
  useEffect(() => {
    const key = chat?.id;
    if (!key || cacheKeyRef.current !== key) return;
    if (!getFeatureFlag('chat_cache')) return;
    setChatEntry('wa_business', key, { messages });
  }, [messages, chat?.id]);

  useEffect(() => {
    if (chat && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chat]);

  // Trocou de conversa: a citação pendente era da conversa anterior.
  useEffect(() => {
    setReplyTo(null);
  }, [chat?.id]);

  // ============================================
  // HELPERS
  // ============================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Pagina o historico para tras quando o operador se aproxima do topo.
  // Mantemos a scroll position estavel medindo a altura ANTES do prepend e
  // ajustando scrollTop pela diferenca depois do paint.
  const loadMoreMessages = useCallback(async () => {
    if (!chat?.id || !hasMoreMessages || loadingMoreMessages || loading) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    setLoadingMoreMessages(true);
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    try {
      const response = await waBusinessService.getMessages(
        chat.id, PAGE_SIZE, messages.length
      );
      const older = response.data || [];
      if (older.length < PAGE_SIZE) setHasMoreMessages(false);
      if (older.length === 0) return;

      const sortedOlder = [...older].sort((a, b) => {
        const timeA = getTimestampMs(a.timestamp) || getTimestampMs(a.created_at);
        const timeB = getTimestampMs(b.timestamp) || getTimestampMs(b.created_at);
        return timeA - timeB;
      });

      // Dedup defensivo por id ou wamid — webhook pode ter materializado
      // mensagens novas durante o fetch.
      setMessages(prev => {
        const seen = new Set();
        prev.forEach(m => { if (m.id) seen.add(m.id); if (m.wamid) seen.add(m.wamid); });
        const filtered = sortedOlder.filter(m => {
          const key = m.id || m.wamid;
          return key && !seen.has(key);
        });
        return [...filtered, ...prev];
      });

      // Restaura scroll position apos o paint da nova lista para o usuario
      // nao "pular" no historico.
      requestAnimationFrame(() => {
        if (!messagesContainerRef.current) return;
        const newHeight = messagesContainerRef.current.scrollHeight;
        messagesContainerRef.current.scrollTop =
          previousScrollTop + (newHeight - previousScrollHeight);
      });
    } catch (err) {
      console.error('[WA Business ChatWindow] loadMore error:', err);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [chat?.id, hasMoreMessages, loadingMoreMessages, loading, messages.length]);

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    // Guards defensivos: nao paginar se nao ha scroll possivel, nao ha
    // mensagens carregadas ainda, ou se o load inicial ainda nao terminou.
    if (scrollHeight <= clientHeight) return;
    if (messages.length === 0 || loading) return;
    if (scrollTop < SCROLL_TOP_THRESHOLD && hasMoreMessages && !loadingMoreMessages) {
      loadMoreMessages();
    }
  };

  // ============================================
  // SEND MESSAGE (with files support)
  // ============================================

  const handleSend = async (e, textOverride = null) => {
    e?.preventDefault();
    const messageText = textOverride || newMessage.trim();
    
    const hasText = messageText && messageText.length > 0;
    const hasFiles = attachedFiles.length > 0;
    
    if (!hasText && !hasFiles) return;
    if (sending) return;

    const filesToSend = [...attachedFiles];
    // Responder citando: a citação vai na PRIMEIRA mensagem que sai (texto,
    // ou a primeira mídia quando não há texto), como no WhatsApp.
    const quoting = messageActionsOn && replyTo ? replyTo : null;
    const quotedOpts = quoting ? { quotedMessageId: quoting.id } : {};
    const quotedBlock = quoting ? quotedFromMessage(quoting) : null;

    // Clear input immediately
    if (!textOverride) {
      setNewMessage('');
    }
    setAttachedFiles([]);
    setWindowError(null);
    
    setSending(true);
    setTimeout(() => setSending(false), 300);

    // Create temp message for optimistic UI
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      wamid: tempId,
      from_me: true,
      from_phone: '',
      to_phone: chat.contact_phone,
      type: hasFiles ? (filesToSend[0]?.type || 'document') : 'text',
      text: messageText || null,
      media_url: hasFiles ? filesToSend[0]?.url : null,
      status: 'pending',
      timestamp: new Date().toISOString(),
      _isTemp: true,
      _attachedFiles: filesToSend,
      ...(quotedBlock ? { quoted: quotedBlock } : {}),
    };
    
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const phone = chat.contact_phone;
      
      console.log('[WA Business ChatWindow] Sending:', {
        phone,
        textLength: messageText?.length || 0,
        filesCount: filesToSend.length,
      });
      
      let result;
      
      if (hasFiles) {
        const fileItems = filesToSend.map(f => ({
          url: f.url,
          type: f.type || 'document',
          filename: f.filename,
          caption: null,
        }));
        if (quoting) {
          // sendMessageWithMedia não repassa a citação: reproduz o mesmo
          // envio (texto primeiro, depois cada arquivo) citando só o 1º.
          const results = [];
          let quotedUsed = false;
          if (hasText) {
            try {
              const textResult = await waBusinessService.sendMessageDirect(phone, messageText, quotedOpts);
              quotedUsed = true;
              results.push({ type: 'text', success: true, data: textResult });
            } catch (e) {
              results.push({ type: 'text', success: false, error: e.message });
            }
          }
          for (const file of fileItems) {
            try {
              const mediaResult = await waBusinessService.sendMedia(
                phone, file.url, file.type, file.caption, file.filename,
                quotedUsed ? {} : quotedOpts
              );
              quotedUsed = true;
              results.push({ type: 'media', success: true, data: mediaResult });
            } catch (e) {
              results.push({ type: 'media', success: false, error: e.message, file: file.filename });
            }
          }
          const allSuccess = results.every(r => r.success);
          result = {
            success: allSuccess,
            partial: results.some(r => r.success) && !allSuccess,
            results,
            chat_id: results.find(r => r.data?.chat_id)?.data?.chat_id,
          };
        } else {
          result = await waBusinessService.sendMessageWithMedia(phone, messageText || null, fileItems);
        }
        
        if (result.partial) {
          console.warn('[WA Business ChatWindow] Partial success:', result.results);
        }
        
        const realId = result.results?.find(r => r.data?.message_id)?.data?.message_id;

        if (realId) {
          setMessages(prev => prev.map(m =>
            m.id === tempId
              ? {
                  ...m,
                  id: realId,
                  wamid: realId,
                  status: 'sent',
                  _isTemp: false,
                }
              : m
          ));
        }
      } else {
        // Pseudo-chat (contato sem conversa previa) usa /send direto, que
        // chama get_or_create_chat no backend antes de enviar. Para chats
        // existentes mantemos a rota /chats/{id}/send (mais explicita).
        if (chat?._isNew) {
          result = await waBusinessService.sendMessageDirect(phone, messageText, quotedOpts);
        } else {
          result = await waBusinessService.sendMessage(chat.id, messageText, quotedOpts);
        }

        if (result.success) {
          const realId = result.message_id;
          if (realId) {
            setMessages(prev => prev.map(m =>
              m.id === tempId
                ? {
                    ...m,
                    id: realId,
                    wamid: realId,
                    status: 'sent',
                    _isTemp: false,
                  }
                : m
            ));
          }
          // Apos o primeiro envio bem-sucedido a partir de pseudo-chat,
          // o backend criou o chat real (com `chat_id` retornado abaixo).
          // Promovemos o pseudo via callback — o Inbox substitui o item
          // na lista de chats e reaponta o selectedChat para o id real.
          if (chat?._isNew && result.chat_id && onChatPromoted) {
            onChatPromoted({
              tempId: chat.id,
              realChatId: result.chat_id,
              phone,
            });
          }
        } else {
          throw new Error(result.error || 'Falha ao enviar');
        }
      }
      
      onMessageSent?.({ text: messageText, files: filesToSend });
      if (quoting) setReplyTo(null);

    } catch (err) {
      console.error('[WA Business ChatWindow] Send error:', err);

      const errorMsg = err.response?.data?.detail || err.message;
      const is24hError = errorMsg && (
        errorMsg.includes('24h') ||
        errorMsg.includes('janela') ||
        errorMsg.includes('131047')
      );

      // Antes desse fix o branch de 24h REMOVIA a tempMessage e mostrava so
      // a caixa amarela — a mensagem reaparecia em vermelho apenas ao trocar
      // de chat (loadMessages buscava do Cosmos, onde o backend ja salvou
      // como failed sincronamente). Agora mantemos a mensagem no state e
      // marcamos como failed na hora — o operador ve o erro no balao
      // imediatamente com o botao "Enviar template" do is24hError.
      setMessages(prev => prev.map(m =>
        m.id === tempId
          ? { ...m, status: 'failed', error_message: errorMsg }
          : m
      ));

      // Mantemos a caixa amarela acima do input como reforço visual quando
      // for o caso especifico de janela 24h — a mensagem do erro ja tem o
      // mesmo conteudo, mas a caixa chama mais atencao logo apos o submit.
      if (is24hError) {
        setWindowError('Fora da janela de 24h. O contato precisa enviar uma mensagem primeiro.');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape' && replyTo) {
      e.preventDefault();
      setReplyTo(null);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  // ============================================
  // MESSAGE HELPERS
  // ============================================

  const formatTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    if (!date) return '';
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Rotulo do dia: um so lugar para os 4 inboxes (paridade B3, 2026-09-17).
  // O helper compartilhado ja absorveu as estrategias do parseTimestamp.
  const formatDate = (timestamp) => formatDayLabel(timestamp) || 'Data desconhecida';

  const getMessageStatusIcon = (status) => {
    const iconStyle = { marginLeft: 4 };
    
    switch (status) {
      case 'read':
        return <CheckCheck size={14} style={{ ...iconStyle, color: '#53bdeb' }} />;
      case 'delivered':
        return <CheckCheck size={14} style={{ ...iconStyle, color: colors.messageTimeSent || 'rgba(255,255,255,0.6)' }} />;
      case 'sent':
        return <Check size={14} style={{ ...iconStyle, color: colors.messageTimeSent || 'rgba(255,255,255,0.6)' }} />;
      case 'pending':
        return <Loader2 size={12} style={{ ...iconStyle, color: colors.messageTimeSent || 'rgba(255,255,255,0.6)', animation: 'spin 1s linear infinite' }} />;
      case 'failed':
        return <AlertCircle size={13} style={{ ...iconStyle, color: colors.danger || '#ef4444' }} />;
      default:
        return null;
    }
  };

  // Texto curto do status (estilo Kommo). 'failed' (ex.: janela de 24h do
  // WhatsApp Business estourada) em vermelho.
  const getStatusLabel = (status) => {
    // A PALAVRA vem do componente compartilhado (paridade B6, 2026-09-17);
    // a cor do tema e o layout do cabecalho continuam deste shell.
    const padrao = colors.messageTimeSent || 'rgba(255,255,255,0.6)';
    const info = statusInfo(status, 'wa_business');
    if (!info) return { text: '', color: padrao };
    if (info.estado === 'failed') return { text: info.texto, color: colors.danger || '#ef4444' };
    return { text: info.texto, color: info.cor || padrao };
  };

  const getMessageText = (msg) => {
    return msg.text || '';
  };

  const isMessageBlocked = (msg) => {
    if (!msg) return true;
    // Apagada pelo cliente: não vai para o agente nem para encaminhar.
    if (msg.deleted) return true;
    if (msg.type === 'text') return !msg.text;
    if (['image', 'audio', 'video', 'document'].includes(msg.type)) {
      return !msg.media_url;
    }
    return true;
  };

  const hasMediaContent = (msg) => {
    return msg.media_url && msg.type !== 'text';
  };

  const getChatName = () => {
    return chat?.contact_name || chat?.contact_phone || 'Desconhecido';
  };

  const getChatAvatar = () => {
    if (chat?.contact_profile_picture_url) {
      return (
        <img 
          src={chat.contact_profile_picture_url} 
          alt="" 
          style={styles.avatarImg}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    
    return (
      <div style={styles.avatarPlaceholder}>
        <User size={24} />
      </div>
    );
  };

  // ============================================
  // ATTACHED FILES HANDLERS
  // ============================================

  const handleRemoveAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAttachedFiles = () => {
    setAttachedFiles([]);
  };

  // ============================================
  // FILE UPLOAD HANDLERS
  // ============================================

  // Anexo (clipe, menu, upload e compressão) vive no AttachButton, componente
  // compartilhado pelos quatro inboxes.
  const handleFilesUploaded = useCallback((files) => {
    setAttachedFiles(prev => [...prev, ...files]);
  }, []);

  const handleUploadError = useCallback((message) => {
    setUploadError(message);
    setTimeout(() => setUploadError(null), 5000);
  }, []);

  // ============================================
  // AUDIO RECORDING HANDLERS (voice note)
  // ============================================

  const handleStartRecording = async () => {
    if (sending || sendingAudio || uploading) return;
    try {
      await audioRecorder.start();
    } catch (err) {
      setUploadError(err.message || 'Nao foi possivel iniciar a gravacao.');
      setTimeout(() => setUploadError(null), 4000);
    }
  };

  const handleCancelRecording = () => {
    audioRecorder.cancel();
  };

  // Para a gravacao, faz upload do audio e envia como voice note. Reusa o
  // mesmo pipeline de midia (sendMessageWithMedia) com type 'audio'.
  const handleStopAndSendAudio = async () => {
    if (sendingAudio) return;
    const file = await audioRecorder.stop();
    if (!file) return;

    setSendingAudio(true);
    setUploadError(null);

    const tempId = `temp-${Date.now()}`;
    const localUrl = URL.createObjectURL(file);
    const tempMessage = {
      id: tempId,
      wamid: tempId,
      from_me: true,
      from_phone: '',
      to_phone: chat.contact_phone,
      type: 'audio',
      text: null,
      media_url: localUrl,
      media_type: file.type,
      status: 'pending',
      timestamp: new Date().toISOString(),
      _isTemp: true,
    };
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const uploaded = await fileUploadService.uploadFile(file, null, { channel: 'wa_business' });
      const result = await waBusinessService.sendMessageWithMedia(
        chat.contact_phone,
        null,
        [{ url: uploaded.url, type: 'audio', filename: uploaded.filename, caption: null }]
      );

      const realId = result.results?.find(r => r.data?.message_id)?.data?.message_id;
      setMessages(prev => prev.map(m =>
        m.id === tempId
          ? {
              ...m,
              id: realId || m.id,
              wamid: realId || m.wamid,
              media_url: uploaded.url,
              status: 'sent',
              _isTemp: false,
            }
          : m
      ));

      onMessageSent?.({ text: null, files: [uploaded] });
    } catch (err) {
      console.error('[WA Business ChatWindow] Audio send error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Falha ao enviar audio.';
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'failed', error_message: errorMsg } : m
      ));
      setUploadError('Falha ao enviar audio.');
      setTimeout(() => setUploadError(null), 4000);
    } finally {
      setSendingAudio(false);
      try { URL.revokeObjectURL(localUrl); } catch (_) {}
    }
  };

  // ============================================
  // AI WORKSPACE HANDLERS
  // ============================================

  const handleExecuteTranslate = async () => {
  if (!newMessage.trim()) return;

  const originalText = newMessage.trim();

  try {
    await aiWorkspace.executeTranslate(originalText, async ({ action, text, languages }) => {
      const results = await aiAgentService.processAction({
        action,
        text,
        languages,
      });
      
      return results.map(r => ({
        ...r,
        originalText: originalText,
      }));
    });
  } catch (error) {
    // 🆕 ADICIONAR: Verificar se é erro de agente não configurado
    if (handleAgentError(error)) return;
    
    // Se não for erro de agente, log normal
    console.error('[ChatWindow] Translate error:', error);
  }
};

  // Preview of selected messages for the "Chamar o Agente" modal context.
  // Recomputed only when the selection or messages change.
  const selectedMessagesPreview = useMemo(() => {
    if (aiWorkspace.selectedMessages.size === 0) return [];
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.wamid;
      return aiWorkspace.selectedMessages.has(msgId);
    });
    const payload = extractMessagesPayload(selectedMsgs, {
      channel: 'wa_business',
      chat,
      operatorName,
    });
    return payload.map(p => ({
      role: p.role,
      text: p.text,
      type: p.type,
      senderLabel: p.role === 'operator' ? 'Você' : (p.sender_name || 'Cliente'),
      timestamp: p.timestamp,
    }));
  }, [aiWorkspace.selectedMessages, messages, chat]);

  const handleExecuteGenerate = async () => {
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';

    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.wamid;
      return aiWorkspace.selectedMessages.has(msgId);
    });

    const messagesPayload = extractMessagesPayload(selectedMsgs, {
      channel: 'wa_business',
      chat,
      operatorName,
    });

    if (messagesPayload.length === 0) return;

    const contextWithMessages = {
      chatId: chat?.id,
      chatName: getChatName(),
      contact_phone: chat?.contact_phone || '',
      channel: 'wa_business',
      operatorName,
      originalMessages: messagesPayload,
    };

    try {
      await aiWorkspace.executeGenerate(messagesPayload, async ({ action, messages: msgs }) => {
        const results = await aiAgentService.processAction({
          action,
          messages: msgs,
          languages: [],
          context: contextWithMessages,
        });

        return results.map(r => ({
          ...r,
          originalMessages: messagesPayload,
        }));
      }, contextWithMessages);
    } catch (error) {
      if (handleAgentError(error)) return;
      console.error('[ChatWindow] Generate error:', error);
    }
  };


  const handleUseResponse = (result) => {
    // Mensagem pronta vinda do AI Workspace: shape da API (attachments/audio
    // snake_case) — converte como o drawer, senão a mídia é descartada em
    // silêncio (bug myCar 07/08, caminho do celular).
    if (result && (Array.isArray(result.attachments) || result.audio)) {
      handleUseSavedMessage(result);
      return;
    }

    setNewMessage(result.text);

    if (result.files && result.files.length > 0) {
      setAttachedFiles(prev => [...prev, ...result.files]);
      console.log('[WA Business ChatWindow] Attached files from AI response:', result.files.length);
    }

    inputRef.current?.focus();

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
      }
    }, 0);
  };

  // Insert a saved message (knowledge base) into the input/attachments, same
  // UX as "Usar resposta" for AI history cards.
  const handleUseSavedMessage = (item) => {
    if (!item) return;

    if (item.text) {
      // Variáveis substituídas NO INPUT (editáveis); o resto o backend
      // resolve no envio.
      setNewMessage(renderVariablesInText(item.text, {
        contactName: getChatName(),
        operatorName: user?.name,
        phone: chat?.contact_phone,
      }));
    }

    // Convert saved-message attachment shape (snake_case) → attachedFiles shape
    // (camelCase) used by the chat send pipeline.
    const incoming = [];
    if (Array.isArray(item.attachments)) {
      for (const a of item.attachments) {
        if (!a?.file_url) continue;
        incoming.push({
          url: a.file_url,
          filename: a.file_name || 'arquivo',
          type: a.type || 'file',
          mimeType: a.mime_type,
          size: a.file_size,
        });
      }
    }
    if (item.audio?.file_url) {
      incoming.push({
        url: item.audio.file_url,
        filename: item.audio.file_name || 'audio.webm',
        type: 'audio',
        mimeType: item.audio.mime_type || 'audio/webm',
        size: undefined,
      });
    }

    if (incoming.length > 0) {
      setAttachedFiles((prev) => [...prev, ...incoming]);
    }

    inputRef.current?.focus();
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
      }
    }, 0);
  };

  // Optimistic update apos enviar um template Meta pelo SavedMessagesDrawer.
  // Antes desse fix a mensagem so aparecia quando o webhook da Meta voltava
  // para o nosso backend, o que dava a impressao de que o envio nao tinha
  // funcionado. Aqui injetamos a mensagem no state imediatamente — o webhook
  // posterior cai em dedup pelo wamid no SSE listener.
  const handleTemplateSent = ({ templateName, templateBody, wamid }) => {
    if (!chat) return;
    const fallbackId = `tmp-${Date.now()}`;
    const tempMessage = {
      id: wamid || fallbackId,
      wamid: wamid || fallbackId,
      chat_id: chat.id,
      from_me: true,
      from_phone: '',
      to_phone: chat.contact_phone || '',
      type: 'template',
      text: templateBody || `[Template: ${templateName}]`,
      status: 'sent',
      timestamp: new Date().toISOString(),
      _isTemp: !wamid,
    };
    setMessages((prev) => {
      // Dedup defensivo — webhook pode ter chegado primeiro.
      if (wamid && prev.some((m) => m.wamid === wamid)) return prev;
      return [...prev, tempMessage];
    });
    setTimeout(() => scrollToBottom(), 100);
  };

  const handleRegenerateTranslate = async (result) => {
  if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.TRANSLATE) <= 0) return;
  
  setRegeneratingId(result.id);
  
  try {
    const originalText = result.originalText || result.text;
    
    await aiWorkspace.regenerateResult(
      result.id, 
      ACTION_TYPES.TRANSLATE,
      async (params) => {
        const results = await aiAgentService.processAction({
          ...params,
          text: originalText,
        });
        
        return results.map(r => ({
          ...r,
          originalText: originalText,
        }));
      },
      { text: originalText, originalText }
    );
  } catch (error) {
    // 🆕 ADICIONAR
    if (handleAgentError(error)) return;
    console.error('[ChatWindow] Regenerate translate error:', error);
  } finally {
    setRegeneratingId(null);
  }
};

  const handleRegenerateGenerate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.GENERATE) <= 0) return;

    setRegeneratingId(result.id);

    try {
      const originalMessages = result.originalMessages || [];

      if (originalMessages.length === 0) {
        console.warn('[ChatWindow] No original messages found for regeneration');
        return;
      }

      const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
      const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';

      await aiWorkspace.regenerateResult(
        result.id,
        ACTION_TYPES.GENERATE,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            messages: originalMessages,
            context: {
              chatId: chat?.id,
              chatName: getChatName(),
              contact_phone: chat?.contact_phone || '',
              channel: 'wa_business',
              operatorName,
            },
          });

          return results.map(r => ({
            ...r,
            originalMessages: originalMessages,
          }));
        },
        { originalMessages }
      );
    } catch (error) {
      if (handleAgentError(error)) return;
      console.error('[ChatWindow] Regenerate generate error:', error);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleExecuteCustom = async (prompt, files = []) => {
    if (!prompt?.trim()) return;

    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const originalPrompt = prompt.trim();
    const originalFiles = Array.isArray(files) ? files : [];

    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.wamid;
      return aiWorkspace.selectedMessages.has(msgId);
    });
    const refMessages = extractMessagesPayload(selectedMsgs, {
      channel: 'wa_business',
      chat,
      operatorName,
    });

    const baseContext = {
      userId: userMetadata.id || 'anonymous',
      chatId: chat?.id,
      chatName: getChatName(),
      contact_phone: chat?.contact_phone || '',
      channel: 'wa_business',
      operatorName,
      messages: refMessages,
    };

    try {
      await aiWorkspace.executeCustom(originalPrompt, async ({ action, prompt: userPrompt }) => {
        const results = await aiAgentService.processAction({
          action,
          prompt: userPrompt,
          files: originalFiles,
          context: baseContext,
        });

        return results.map(r => ({
          ...r,
          originalPrompt: originalPrompt,
          originalFiles: originalFiles,
        }));
      }, baseContext);
    } catch (error) {
      if (handleAgentError(error)) return;
      console.error('[ChatWindow] Custom prompt error:', error);
    }
  };

  // Roleta de transferencia: distribui o contato atual pelo grupo escolhido
  // (chip do AI Workspace). O header de responsavel atualiza sozinho via SSE
  // CONTACT_ASSIGNMENT_CHANGED; o spinner no chip dura ate o assign responder.
  const handleExecuteRoulette = async (groupId) => {
    const phone = chat?.contact_phone;
    if (!phone || rouletteBusyId) return;
    setRouletteBusyId(groupId);
    try {
      await assignToRoulette(phone, groupId);
    } catch (err) {
      console.error('[ChatWindow] roleta:', err);
    } finally {
      setRouletteBusyId(null);
    }
  };


  const handleRegenerateCustom = async (result) => {
  if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.CUSTOM) <= 0) return;

  setRegeneratingId(result.id);

  try {
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const originalPrompt = result.originalPrompt || '';

    if (!originalPrompt) {
      console.warn('[ChatWindow] No original prompt found for regeneration');
      return;
    }

    await aiWorkspace.regenerateResult(
      result.id,
      ACTION_TYPES.CUSTOM,
      async (params) => {
        const results = await aiAgentService.processAction({
          ...params,
          prompt: originalPrompt,
          context: {
            userId: userMetadata.id || 'anonymous',
            chatId: chat?.id,
            chatName: getChatName(),
            contact_phone: chat?.contact_phone || '',
            channel: 'wa_business',
            operatorName,
          },
        });

        return results.map(r => ({
          ...r,
          originalPrompt: originalPrompt,
        }));
      },
      { originalPrompt }
    );
  } catch (error) {
    // 🆕 ADICIONAR
    if (handleAgentError(error)) return;
    console.error('[ChatWindow] Regenerate custom error:', error);
  } finally {
    setRegeneratingId(null);
  }
};

  const handleToggleMessageSelection = (msgId, msg) => {
    const isBlocked = isMessageBlocked(msg);
    const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
    const isCurrentlySelected = aiWorkspace.selectedMessages.has(msgId);
    
    if (isCurrentlySelected) {
      aiWorkspace.toggleMessageSelection(msgId, msg);
      return;
    }
    
    if (isBlocked || isAtLimit) {
      return;
    }

    aiWorkspace.toggleMessageSelection(msgId, msg);
  };

  // ============================================
  // AÇÕES DE MENSAGEM (flag message_actions)
  // ============================================

  // Tipo de mídia da mensagem para a citação (null para texto/template).
  const quotedMediaTypeOf = (msg) => (
    ['image', 'video', 'audio', 'document', 'sticker'].includes(msg?.type) ? msg.type : null
  );

  // "Responder": guarda a mensagem citada (normalizada) e volta o foco ao
  // campo de texto. A prévia aparece acima do composer.
  const startReply = (msg) => {
    if (!msg || msg.deleted) return;
    setReplyTo({
      id: msg.id,
      externalId: msg.wamid || null,
      text: msg.text || '',
      fromMe: !!msg.from_me,
      mediaType: quotedMediaTypeOf(msg),
      senderName: msg.from_me ? (msg.sent_by_name || msg.agent_name || null) : (chat?.contact_name || null),
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelReply = () => setReplyTo(null);

  // Reagir: otimista na bolha; se a Cloud API recusar, volta o valor
  // anterior e avisa na faixa de erro do composer (a mesma do upload).
  const react = async (msg, emoji) => {
    if (!msg?.id || msg._isTemp) return;
    const previous = msg.reaction_from_me || null;
    const next = emoji || null;
    const apply = (value) => setMessages(prev => prev.map(m => (
      m.id === msg.id ? { ...m, reaction_from_me: value } : m
    )));
    apply(next);
    try {
      await waBusinessService.sendReaction(msg.id, next);
    } catch (err) {
      console.error('[WA Business ChatWindow] Reaction error:', err);
      apply(previous);
      setUploadError(err.response?.data?.detail || 'Não foi possível reagir à mensagem.');
      setTimeout(() => setUploadError(null), 4000);
    }
  };

  // "Encaminhar" pelo menu da bolha: a seleção passa a ser só esta mensagem
  // e o modal de destinos abre direto (sem passar pela barra de seleção).
  const forwardSingle = (msgId, msg) => {
    if (isMessageBlocked(msg)) return;
    aiWorkspace.clearSelection();
    aiWorkspace.toggleMessageSelection(msgId, msg);
    setShowForwardModal(true);
  };

  const copyMessageText = async (msg) => {
    const text = (msg?.text || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('[WA Business ChatWindow] Copy error:', err);
      setUploadError('Não foi possível copiar o texto.');
      setTimeout(() => setUploadError(null), 3000);
    }
  };

  // Clique na citação: rola até a mensagem original e a destaca por 2 s.
  const jumpToMessage = (quoted) => {
    if (!quoted) return;
    const target = messages.find((m) => (
      (quoted.id && m.id === quoted.id) || (quoted.external_id && m.wamid === quoted.external_id)
    ));
    if (!target) return;
    const el = document.getElementById(`msg-${target.id || target.wamid}`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('msg-row--flash');
    setTimeout(() => el.classList.remove('msg-row--flash'), 2000);
  };

  // ============================================
  // FORWARD (Encaminhar) — seleção própria + reenvio por canal
  // ============================================

  // Extrai o que reenviar de uma mensagem (shape WA Business). Texto vira
  // { text }; mídia vira { mediaUrl, mediaType, caption, filename }. Retorna
  // null quando não há nada reenviável (sem texto e sem media_url). Templates
  // não são reenviáveis como mensagem livre — caem aqui só se tiverem texto.
  const extractForwardable = (msg) => {
    if (!msg) return null;
    const isMediaType = ['image', 'video', 'audio', 'document'].includes(msg.type);

    if (isMediaType && msg.media_url) {
      return {
        mediaUrl: msg.media_url,
        mediaType: msg.type,
        caption: msg.type === 'image' || msg.type === 'video' ? (msg.text || null) : null,
        filename: msg.media_filename || null,
      };
    }

    if (msg.text && msg.text.trim()) {
      return { text: msg.text.trim() };
    }

    return null;
  };

  // Destinos: reaproveita a lista de chats já carregada no Inbox. Pula
  // pseudo-chats sem telefone real.
  const forwardDestinations = useMemo(() => {
    const seen = new Set();
    const out = [];
    (chats || []).forEach((c) => {
      const digits = (c.contact_phone || '').replace(/\D/g, '');
      if (!digits) return;
      if (seen.has(digits)) return;
      seen.add(digits);
      out.push({
        id: c.id || digits,
        name: c.contact_name || c.contact_phone || 'Desconhecido',
        phone: c.contact_phone || digits,
        _number: digits,
        avatarUrl: c.contact_profile_picture_url || null,
      });
    });
    return out;
  }, [chats]);

  // Reenvia, para cada destino, cada mensagem selecionada (na ordem em que
  // aparecem). Erros são por destino — uma falha não aborta os demais.
  const handleForwardSend = async (selectedDestinations) => {
    const toForward = messages
      .filter((msg) => aiWorkspace.selectedMessages.has(msg.id || msg.wamid))
      .map((msg) => extractForwardable(msg))
      .filter(Boolean);

    const outcomes = [];
    for (const dest of selectedDestinations) {
      const number = dest._number;
      let ok = true;
      try {
        for (const item of toForward) {
          if (item.text) {
            await waBusinessService.sendMessageDirect(number, item.text);
          } else if (item.mediaUrl) {
            await waBusinessService.sendMedia(
              number,
              item.mediaUrl,
              item.mediaType,
              item.caption,
              item.filename
            );
          }
        }
      } catch (err) {
        console.error('[WA Business ChatWindow] Forward failed for', dest.name, err);
        ok = false;
      }
      outcomes.push({ id: dest.id, name: dest.name, ok });
    }

    return outcomes;
  };

  // Group messages by date (maintaining chronological order)
  const groupedMessages = useMemo(() => {
    const groups = {};
    const dateOrder = []; // Track order of dates as they appear

    // Card de transferência entra na MESMA linha do tempo das mensagens
    // (ordem cronológica, como no wa-personal). Eventos anteriores à primeira
    // mensagem carregada ficam de fora — senão a paginação empurraria cards
    // "órfãos" para o topo sem o contexto da conversa da época.
    //
    // parseTimestamp SEMPRE (não new Date direto): o container mistura ISO
    // (envios do sistema) com epoch-string do webhook da Meta ('1786024951'),
    // que o new Date não entende — metade da thread caía em ts=0 e o
    // agrupador por data costurava os grupos fora de ordem: o template de
    // 10/07 renderizava DEPOIS das mensagens de hoje (caso Gelo Americano,
    // contato 555196956456, 06/08).
    const toSeconds = (t) => {
      const ms = getTimestampMs(t);
      return ms ? Math.floor(ms / 1000) : 0;
    };
    const firstTs = messages.length
      ? Math.min(...messages.map((m) => toSeconds(m.timestamp || m.created_at) || Infinity))
      : null;
    const timeline = [
      ...messages.map((m) => ({
        item: m,
        ts: toSeconds(m.timestamp || m.created_at),
        when: m.timestamp || m.created_at,
      })),
      ...threadEvents
        .filter((e) => firstTs === null || e.ts >= firstTs)
        .map((e) => ({ item: e, ts: e.ts, when: e.event?.timestamp })),
    ].sort((a, b) => a.ts - b.ts);

    timeline.forEach(({ item, when }) => {
      const dateKey = formatDate(when);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
        dateOrder.push(dateKey);
      }
      groups[dateKey].push(item);
    });

    // Return as ordered entries to maintain chronological order
    const orderedGroups = {};
    dateOrder.forEach(key => {
      orderedGroups[key] = groups[key];
    });

    return orderedGroups;
  }, [messages, threadEvents]);

  // ============================================
  // STYLES (Padronizado com WhatsApp Pessoal - cores verdes)
  // ============================================

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
      // Superficie base do chat. A faixa do AIWorkspace (wrapper transparente)
      // herda esta cor, ficando igual ao header e ao fundo do ChatList.
      backgroundColor: colors.bgPrimary,
    },
    emptyContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgSecondary,
    },
    emptyContent: {
      textAlign: 'center',
      color: colors.textSecondary,
    },
    emptyTitle: {
      fontSize: '32px',
      fontWeight: 300,
      color: colors.textPrimary,
      marginBottom: '12px',
    },
    emptyText: {
      fontSize: '14px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '4px' : '8px',
      padding: isMobile ? '8px 8px' : '10px 16px',
      // iOS PWA (standalone): empurra o conteudo do header para BAIXO da status
      // bar (wifi/horario/bateria) para o botao de voltar nao ficar inacessivel
      // sob a barra. env() = 0 em telas sem notch/desktop, entao e seguro sempre.
      ...(isMobile && { paddingTop: 'calc(8px + env(safe-area-inset-top))' }),
      // Mesma cor do fundo do ChatList (branco/navy), sem o cinza do headerBg.
      backgroundColor: colors.bgPrimary,
      // Borda dura trocada por uma sombra bem leve para separar do conteudo.
      boxShadow: colors.shadowSm,
      minHeight: '60px',
      zIndex: 1,
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    headerDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerName: {
      fontWeight: 500,
      fontSize: '16px',
      color: colors.textPrimary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: isMobile ? '46vw' : 'none',
    },
    headerStatus: {
      fontSize: '13px',
      color: colors.textSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '6px' : '8px',
      flexShrink: 0,
    },
    headerActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
    },
    messagesLoading: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messagesEmpty: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messageGroup: {
      marginBottom: '16px',
    },
    dateDivider: {
      display: 'flex',
      justifyContent: 'center',
      margin: '16px 0',
    },
    dateDividerText: {
      backgroundColor: colors.bgTertiary,
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      color: colors.textSecondary,
      boxShadow: colors.shadowSm,
    },
    messageRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginBottom: '2px',
      padding: '2px 0',
      borderRadius: '8px',
      transition: 'background-color 0.15s ease',
    },
    messageRowSelectedSent: {
      backgroundColor: `${workspaceColors.roleOperator}15`,
      margin: '2px -8px',
      padding: '6px 8px',
      borderRight: `3px solid ${workspaceColors.roleOperator}`,
    },
    messageRowSelectedReceived: {
      backgroundColor: `${workspaceColors.roleCustomer}15`,
      margin: '2px -8px',
      padding: '6px 8px',
      borderLeft: `3px solid ${workspaceColors.roleCustomer}`,
    },
    messageBubbleWrapper: {
      flex: 1,
      display: 'flex',
      position: 'relative',
    },
    messageSent: {
      justifyContent: 'flex-end',
    },
    messageReceived: {
      justifyContent: 'flex-start',
    },
    messageContentSent: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageSent,
      color: colors.messageSentText,
      borderTopRightRadius: '2px',
    },
    messageContentReceived: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageReceived,
      color: colors.messageReceivedText,
      borderTopLeftRadius: '2px',
    },
    messageFailed: {
      backgroundColor: `${colors.danger || '#ef4444'}20`,
      border: `1px solid ${colors.danger || '#ef4444'}40`,
    },
    messageText: {
      fontSize: '14px',
      lineHeight: 1.4,
      // overflowWrap: 'anywhere' garante quebra mesmo em URLs/strings longas
      // sem espacos, evitando o scroll horizontal feio no balao.
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      margin: 0,
      minWidth: 0,
    },
    messageMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '4px',
      marginTop: '4px',
    },
    // Marca "editada" ao lado do horário (mesmo estilo do wa-personal).
    messageEdited: {
      fontSize: '10px',
      fontStyle: 'italic',
      color: 'var(--text-muted, #94a3b8)',
    },
    messageEditedSent: {
      fontSize: '10px',
      fontStyle: 'italic',
      color: colors.messageTimeSent || 'rgba(255,255,255,0.6)',
    },
    // Corpo de mensagem apagada pelo cliente (revoke).
    messageDeleted: {
      fontSize: '13px',
      fontStyle: 'italic',
      color: 'var(--text-muted, #94a3b8)',
      margin: 0,
    },
    senderHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '11px',
      fontWeight: 400,
      color: colors.messageTimeSent || 'rgba(255,255,255,0.6)',
      marginBottom: '4px',
      maxWidth: '100%',
    },
    messageTimeSent: {
      fontSize: '11px',
      color: colors.messageTimeSent,
    },
    messageTimeReceived: {
      fontSize: '11px',
      color: colors.textTertiary,
    },
    messageMedia: {
      maxWidth: '300px',
      borderRadius: '8px',
      marginBottom: '4px',
    },
    scrollButton: {
      position: 'absolute',
      bottom: '80px',
      right: '24px',
      width: '42px',
      height: '42px',
      borderRadius: '50%',
      backgroundColor: colors.bgPrimary,
      border: 'none',
      boxShadow: colors.shadowMd,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
      zIndex: 10,
    },
    windowError: {
      backgroundColor: `${colors.warning || '#f59e0b'}15`,
      borderTop: `1px solid ${colors.warning || '#f59e0b'}30`,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    windowErrorText: {
      flex: 1,
      fontSize: '13px',
      color: colors.warning || '#f59e0b',
    },
    inputContainer: {
      // Mesma superficie do header (bgPrimary). O campo em si usa bgTertiary
      // abaixo para nao sumir contra esse fundo.
      backgroundColor: colors.bgPrimary,
    },
    // Input padronizado com o TextAudioInput do onboarding: bordas
    // arredondadas, anexo e enviar circulares dentro do mesmo container.
    inputForm: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px 12px',
      // iOS PWA (standalone): levanta o input acima do home indicator para nao
      // colar na borda inferior nem deixar a "margem preta" sob a barra do Safari.
      ...(isMobile && { paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }),
    },
    inputWrapper: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      borderRadius: '24px',
      border: `1px solid ${colors.inputBorder}`,
      // Mesma cor interna do search do ChatList (bgTertiary) para destacar do
      // fundo bgPrimary do container e nao "sumir".
      backgroundColor: colors.bgTertiary,
      padding: '4px 6px',
      transition: 'border-color 0.2s ease',
    },
    inputActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '34px',
      height: '34px',
      minWidth: '34px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background-color 0.15s ease',
    },
    textarea: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      padding: '6px 4px',
      // 16px no mobile evita o zoom automático do iOS ao focar o compositor.
      fontSize: isMobile ? '16px' : '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      maxHeight: '100px',
      lineHeight: 1.4,
      fontFamily: 'inherit',
    },
    sendBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '34px',
      height: '34px',
      minWidth: '34px',
      border: 'none',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'opacity 0.15s ease',
    },
    sendBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  // ============================================
  // RENDER
  // ============================================

  if (!chat) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyContent}>
          <h3 style={styles.emptyTitle}>{brandName() || 'Conversas'}</h3>
          <p style={styles.emptyText}>Selecione uma conversa para começar a enviar mensagens</p>
        </div>
      </div>
    );
  }

  const canSend = (newMessage.trim() || attachedFiles.length > 0) && !uploading;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        {/* Mobile: botão voltar (←) volta para a lista, tela cheia estilo WhatsApp. */}
        {isMobile && onBack && (
          <button
            type="button"
            onClick={onBack}
            title="Voltar"
            style={{
              ...styles.headerActionBtn,
              color: colors.textPrimary,
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={24} />
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowContactDetails(true)}
          title="Ver dados do contato"
          style={{
            ...styles.headerInfo,
            background: 'transparent', border: 'none',
            padding: '2px 4px', borderRadius: 8,
            cursor: 'pointer', textAlign: 'left',
            flex: isMobile ? 1 : undefined,
            minWidth: isMobile ? 0 : undefined,
          }}
        >
          <div style={styles.headerAvatar}>{getChatAvatar()}</div>
          <div style={{ ...styles.headerDetails, minWidth: isMobile ? 0 : undefined }}>
            <span style={styles.headerName}>{getChatName()}</span>
            <span style={styles.headerStatus}>
              {chat.contact_name ? `+${chat.contact_phone}` : ''}
            </span>
            {(() => {
              // Thread interno da Ponte do Gestor: quem responde aqui é o
              // Gestor (para a equipe), não o atendimento de lead — dizer
              // "Agente pausado" enquanto ele conversa era informação errada
              // (item 12 da manutenção de 05/08).
              if (gestorInternal) {
                return (
                  <span style={{ fontSize: '11px', color: colors.accentPrimary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Bot size={10} />
                    Gestor ativo para a equipe interna
                  </span>
                );
              }
              if (assignedMember) {
                return (
                  <span style={{ fontSize: '11px', color: '#d97706', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Bot size={10} />
                    {`Em atendimento com ${assignedMember.name || 'membro da equipe'}`}
                  </span>
                );
              }
              if (channelPaused) {
                return (
                  <span style={{ fontSize: '11px', color: '#d97706', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Bot size={10} />
                    Agente pausado neste canal
                  </span>
                );
              }
              const chatPhone = chat.contact_phone;
              // `scheduled` e ligado para o motor (autopilot_scope.ACTIVE_MODES).
              const isActive = (autopilotMode === 'all' || autopilotMode === 'scheduled')
                ? !(blockedContacts || []).some(c => c.replace(/\D/g, '') === chatPhone?.replace(/\D/g, ''))
                : (autopilotContacts || []).includes(chatPhone);
              // Responsável agente específico prevalece sobre o principal
              // (mesma resolução do agent_router).
              const agentName = (assignedTo?.type === 'agent' && assignedTo.name) || activeAgentName;
              // Mesmo selo da lista e da ficha (WhoAnswersBadge): agente
              // atribuido e automatico com a mesma cor e o mesmo texto.
              return isActive && agentAnswers && agentName ? (
                <WhoAnswersBadge variant="text" assigned={assignedTo?.type === 'agent' ? assignedTo : null} autoAgentName={activeAgentName || 'Agente IA'} />
              ) : null;
            })()}
          </div>
        </button>
        <div style={styles.headerActions}>
          {/* Controle de agente por contato movido para o Transferir contato
              (membro / agente / ninguem). "Transferir para ninguem" pausa o
              agente; transferir para um agente reativa. Fonte unica = assigned_to
              (ver agent_router / TransferContactModal) — sem botao dedicado. */}
          <RequiredFieldsSignal
            contactPhone={chat.contact_phone || chat.id || ''}
            colors={colors}
            refreshKey={contactLabels.count}
            onClick={() => setShowContactDetails(true)}
          />
          <RequiredFieldsPrompt
            contactKey={chat.contact_phone || chat.id || ''}
            colors={colors}
          />
          <FollowupBadge
            contactPhone={chat.contact_phone || chat.id || ''}
            colors={colors}
            refreshKey={scheduleVersion}
            onNavigateToFollowup={(id) => window.open(`/followups?id=${id}`, '_blank')}
          />
          {/* Encaminhar: aparece quando há mensagens selecionadas (a MESMA
              seleção do aiWorkspace/Chamar o Agente) e abre o modal de destinos. */}
          {!messageActionsOn && aiWorkspace.selectedMessages.size > 0 && (
            <button
              onClick={() => setShowForwardModal(true)}
              style={styles.headerActionBtn}
              title={`Encaminhar ${aiWorkspace.selectedMessages.size} ${aiWorkspace.selectedMessages.size === 1 ? 'mensagem' : 'mensagens'}`}
            >
              <Forward size={20} />
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowHeaderMenu(!showHeaderMenu)}
              style={styles.headerActionBtn}
              title="Mais opções"
            >
              <MoreVertical size={20} />
            </button>
            {showHeaderMenu && (
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                  onClick={() => setShowHeaderMenu(false)}
                />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                  backgroundColor: colors.bgPrimary, borderRadius: '10px',
                  border: `1px solid ${colors.borderColor}`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 100, minWidth: '210px', overflow: 'hidden',
                  padding: '6px 0',
                }}>
                  <button
                    onClick={() => {
                      setShowHeaderMenu(false);
                      setSavedDrawerInitialTab('saved');
                      setShowSavedDrawer(true);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <History size={15} color={colors.accentPrimary} />
                    Mensagens Prontas
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowScheduleModal(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Zap size={15} color={colors.accentPrimary} />
                    Agendar Follow-up
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowLabelPicker(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Tags size={15} color={colors.accentPrimary} />
                    {contactLabels.hasLabels ? 'Alterar funil/etapa' : 'Definir funil/etapa'}
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowTransferModal(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <UserCheck size={15} color="#f59e0b" />
                    Transferir contato
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowFeedbackModal(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <MessageSquarePlus size={15} color="#10B981" />
                    Adicionar comentário
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowCreateNotification(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Bell size={15} color={colors.accentPrimary} />
                    Criar notificação
                  </button>
                  <div style={{ height: 1, backgroundColor: colors.borderColor, margin: '6px 14px' }} />
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowWorkspaceConfig(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Settings size={15} color={colors.textSecondary} />
                    Configurar Workspace
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Lead unificado nos 3 canais (flag lead_multichannel): "Também no
          Instagram" com botão para abrir a conversa do outro canal. Mesmo
          componente nos 4 inboxes. */}
      <ContactChannelChips variant="bar" contactKey={chat.contact_phone || ''} channel="wa_business" colors={colors} />

      {/* Handoff Banner — fundo levemente azulado (não cor de pele),
          botões filled no padrão dos CTAs da plataforma (verde forte / azul forte). */}
      {isHandoff && !handoffBannerDismissed && (
        <HandoffBanner
          theme={theme}
          colors={colors}
          // Quem transferiu foi o agente do handoff, que numa conta
          // multi-agente pode nao ser o principal. Mesmo fallback dos outros
          // tres shells (D8 do sweep 2B).
          agentName={handoffMetadata?.handoff_agent_name || activeAgentName}
          reason={handoffMetadata?.handoff_reason || handoffMetadata?.reason}
          // Handoff sem destino vai para um humano (admin ou o proprio
          // usuario): o evento chega antes da atribuicao, entao o nome vem
          // do responsavel quando o metadata nao traz.
          targetName={handoffMetadata?.handoff_target_name || assignedMember?.name}
          onAddComment={() => setShowFeedbackModal(true)}
          onResume={() => onResumeAgent?.(chat.contact_phone)}
          onDismiss={dismissHandoffBanner}
        />
      )}

      {/* Schedule Message Modal (drawer no chat) */}
      <ScheduleMessageModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        contactPhone={chat.contact_phone || chat.id || ''}
        contactName={chat.contact_name || ''}
        colors={colors}
        channel="wa_business"
        onScheduled={() => setScheduleVersion(v => v + 1)}
        displayMode="drawer"
      />

      {/* Label Picker Modal (drawer no chat) */}
      <LabelPickerModal
        isOpen={showLabelPicker}
        onClose={() => { setShowLabelPicker(false); contactLabels.reload(); }}
        contactPhone={chat.contact_phone || chat.id || ''}
        contactName={chat.contact_name || ''}
        colors={colors}
        displayMode="drawer"
      />

      {/* Gerenciar tags do contato (múltiplas, criar na hora) */}
      <ContactTagsManagerModal
        isOpen={showTagsManager}
        onClose={() => setShowTagsManager(false)}
        contactPhone={chat.contact_phone || chat.id || ''}
        contactName={chat.contact_name || ''}
        colors={colors}
        displayMode="drawer"
      />

      {/* Transferência de responsável (drawer no chat) */}
      <TransferContactModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        contactPhone={chat.contact_phone || chat.id || ''}
        contactName={chat.contact_name || ''}
        colors={colors}
        displayMode="drawer"
        channelPaused={channelPaused}
      />


      {/* Contact Feedback Modal (drawer no chat) */}
      <ContactFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        contactPhone={chat.contact_phone || chat.id || ''}
        contactName={chat.contact_name || ''}
        colors={colors}
        displayMode="drawer"
      />

      {/* Mensagens Prontas — inclui aba de Templates Meta porque estamos no
          WA Business. O drawer cuida de listar e disparar templates. */}
      <SavedMessagesDrawer
        isOpen={showSavedDrawer}
        onClose={() => setShowSavedDrawer(false)}
        colors={colors}
        channel="wa_business"
        contactPhone={chat.contact_phone || chat.id || ''}
        contactName={chat.contact_name || ''}
        onUseMessage={handleUseSavedMessage}
        onMessageSent={handleTemplateSent}
        initialTab={savedDrawerInitialTab}
      />

      {/* Configurar Workspace — toggles das tags do AI Workspace. */}
      <WorkspaceConfigDrawer
        isOpen={showWorkspaceConfig}
        onClose={() => setShowWorkspaceConfig(false)}
        colors={colors}
        config={workspaceConfigState.config}
        onToggle={workspaceConfigState.toggle}
        transferRoulettes={transferRoulettes}
      />

      {/* Criar notificação interna ligada ao contato atual (menu do chat) */}
      <CreateNotificationModal
        isOpen={showCreateNotification}
        onClose={() => setShowCreateNotification(false)}
        contactName={getChatName()}
        onSubmit={async ({ title, body, scheduled_at }) => {
          await createNotification({
            title,
            body,
            scheduled_at,
            contact_phone: chat?.contact_phone || null,
            chat_id: chat?.id || null,
            channel: 'wa_business',
            show_toast: false,
          });
        }}
      />

      {/* Drawer "Dados do contato" — abre ao clicar no avatar/nome no header.
          Envolto em ErrorBoundary: se o drawer falhar, o ChatWindow continua
          vivo (volta, lê e responde o chat) em vez de cair na tela preta. */}
      <ErrorBoundary
        name="contact-drawer"
        resetKeys={[showContactDetails, chat.contact_phone || '']}
        fallback={({ reset }) => (showContactDetails ? (
          <DrawerErrorFallback
            colors={colors}
            onClose={() => { reset(); setShowContactDetails(false); }}
          />
        ) : null)}
      >
        <ContactDetailsDrawer
          isOpen={showContactDetails}
          onClose={() => setShowContactDetails(false)}
          colors={colors}
          source="wa_business"
          channel="wa_business"
          contactKey={chat.contact_phone || ''}
          companyContactId={chat.company_contact_id || null}
          chatId={chat.id}
          contactPhone={chat.contact_phone || ''}
          contactName={chat.contact_name || ''}
          contactProfilePicUrl={chat.contact_profile_picture_url}
          autoAgentName={agentAnswers ? ((assignedTo?.type === 'agent' && assignedTo.name) || activeAgentName) : null}
          onNameUpdated={(newName) => {
            onContactNameUpdated?.({ chatId: chat.id, newName });
          }}
          onDeleted={() => onContactDeleted?.({ chatId: chat.id })}
          onNavigateToFollowup={(id) => window.open(`/followups?id=${id}`, '_blank')}
        />
      </ErrorBoundary>

      {/* Messages */}
      <div
        style={styles.messagesContainer}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading && messages.length === 0 ? (
          // Primeira carga da conversa: skeleton de bolhas no lugar do spinner
          // central (componente compartilhado pelos 4 inboxes).
          <MessagesSkeleton />
        ) : loading ? (
          <div style={styles.messagesLoading}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
            <span>Carregando mensagens...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.messagesEmpty}>
            <p style={{ fontSize: '16px', marginBottom: '4px' }}>Nenhuma mensagem ainda</p>
            <span style={{ fontSize: '14px', color: colors.textTertiary }}>
              Envie uma mensagem para iniciar a conversa
            </span>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date} style={styles.messageGroup}>
              <div style={styles.dateDivider}>
                <span style={styles.dateDividerText}>{date}</span>
              </div>
              {msgs.map((msg, index) => {
                // Card de sistema (transferência): entra na mesma lista para
                // respeitar a ordem cronológica, mas não é balão de mensagem.
                if (msg.__threadEvent) {
                  return (
                    <ThreadEventCard key={msg.id} event={msg.event} colors={colors} />
                  );
                }

                // Observabilidade de tools: doc tool_event é card de sistema.
                if (msg.tool_event || msg.type === 'tool_event') {
                  return (
                    <ToolUsageCard
                      key={msg.id || msg.wamid}
                      toolEvent={msg.tool_event}
                      agentName={msg.agent_name}
                      timestamp={msg.timestamp}
                      colors={colors}
                    />
                  );
                }

                const isSent = msg.from_me;
                // Remetente (estilo Kommo): agente mostra o nome do agente;
                // humano (membro/admin via workspace) mostra sent_by_name; o
                // próprio dono mostra o próprio nome. Templates do WhatsApp
                // Business são enviados pelo agente → caem em isAgentMsg.
                // Follow-up gerado pela IA (source "system_send") traz agent_id:
                // é mensagem do agente, com o nome dele e o ícone de robô (2026-09-16).
                const isAgentMsg = msg.source === 'agent_autopilot' || msg.agent_response || msg.originated_by_agent || !!msg.agent_id;
                // Thread unificada: mensagem espelhada de OUTRA conexão da empresa
                // mostra o remetente de ORIGEM ("enviado por X"), não o dono deste
                // workspace.
                // A autoria vem da PRÓPRIA mensagem (agent_name): numa conta
                // com vários agentes, activeAgentName é o agente PRINCIPAL do
                // workspace e rotularia toda bolha automática com o mesmo nome,
                // mesmo depois da transferência. Fica como último recurso, para
                // as mensagens antigas gravadas sem autoria.
                // Regra única dos 5 inboxes (utils/messageAuthor): agente com o
                // nome COMPLETO ("Agente SAC" não vira "Agente"), membro com o
                // primeiro nome e eco do app ("whatsapp_app") como "Pelo app do
                // WhatsApp" em vez do nome de quem está logado.
                const author = isSent
                  ? messageAuthor(msg, { activeAgentName, ownerFallbackName: user?.name || null })
                  : null;
                const mirrorVia = msg.is_mirror ? (msg.source_connection_name || null) : null;
                const msgId = msg.id || msg.wamid || `msg-${index}`;
                const isSelected = aiWorkspace.selectedMessages.has(msgId);
                const isHovered = hoveredMessage === msgId;
                const showCheckbox = isHovered || isSelected || aiWorkspace.selectedMessages.size > 0;
                const msgIsBlocked = isMessageBlocked(msg);
                const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
                const isFailed = msg.status === 'failed';

                const selectedRowStyle = isSelected
                  ? (isSent ? styles.messageRowSelectedSent : styles.messageRowSelectedReceived)
                  : {};
                const contactFirstName = (chat?.contact_name || chat?.contact_phone || 'Cliente').split(' ')[0];
                const isDeleted = !!msg.deleted;
                const msgHasText = !!(msg.text && msg.text.trim());
                // Menu "⌄" da bolha: só com a flag, nunca em apagada nem na
                // bolha otimista (ainda sem id real para reagir/citar).
                const showActionMenu = messageActionsOn && !isDeleted && !msg._isTemp && !!msg.id;
                const editReason = showActionMenu
                  ? whyCannotEdit({
                      fromMe: isSent,
                      hasText: msgHasText,
                      hasMedia: !!hasMediaContent(msg),
                      timestampMs: getTimestampMs(msg.timestamp || msg.created_at),
                      deleted: isDeleted,
                    }, msgCaps)
                  : '';

                return (
                  <div
                    key={msgId}
                    id={`msg-${msgId}`}
                    className={`msg-row${isSelected ? ' msg-row--selected' : ''}`}
                    style={{
                      ...styles.messageRow,
                      ...selectedRowStyle,
                    }}
                    onMouseEnter={() => setHoveredMessage(msgId)}
                    onMouseLeave={() => setHoveredMessage(null)}
                  >
                    {/* Checkbox de seleção (aiWorkspace): seleciona mensagens
                        para o agente E para encaminhar (ícone no header). */}
                    <MessageCheckbox
                      isSelected={isSelected}
                      isVisible={showCheckbox}
                      onToggle={() => handleToggleMessageSelection(msgId, msg)}
                      isBlocked={msgIsBlocked}
                      isAtLimit={isAtLimit && !isSelected}
                      blockReason={msgIsBlocked ? (isDeleted ? 'Mensagem apagada' : 'Apenas mensagens de texto') : ''}
                    />

                    {/* Message bubble */}
                    <div
                      style={{
                        ...styles.messageBubbleWrapper,
                        ...(isSent ? styles.messageSent : styles.messageReceived),
                        // Com a flag, os chips de reação ocupam a linha de
                        // baixo da bolha (flexBasis 100%).
                        ...(messageActionsOn ? { flexWrap: 'wrap' } : {}),
                      }}
                    >
                      {isSelected && (
                        <MessageRolePill
                          role={isSent ? 'operator' : 'customer'}
                          label={isSent ? 'Sua mensagem' : contactFirstName}
                          side={isSent ? 'right' : 'left'}
                        />
                      )}

                      <div style={{
                        ...(isSent ? styles.messageContentSent : styles.messageContentReceived),
                        ...(isFailed ? styles.messageFailed : {}),
                        ...(showActionMenu ? { position: 'relative' } : {}),
                      }}>
                        {showActionMenu && (
                          <MessageActionMenu
                            fromMe={isSent}
                            colors={colors}
                            caps={msgCaps}
                            editReason={editReason}
                            hasText={msgHasText}
                            currentReaction={msg.reaction_from_me || null}
                            forwardReason={msgIsBlocked ? 'Sem conteúdo para encaminhar' : ''}
                            onReply={() => startReply(msg)}
                            onReact={(emoji) => react(msg, emoji)}
                            onForward={() => forwardSingle(msgId, msg)}
                            onSelect={() => handleToggleMessageSelection(msgId, msg)}
                            onCopy={() => copyMessageText(msg)}
                            onEdit={() => {}}
                            deleteReason={msgCaps.whyDelete || 'Este canal não apaga mensagem enviada'}
                            alwaysVisible={isMobile}
                          />
                        )}
                        {/* Cabeçalho estilo Kommo (bolha enviada): horário,
                            nome do remetente, ícone e texto de status numa
                            linha só, em cinza discreto (sem negrito). */}
                        {isSent && (() => {
                          const st = getStatusLabel(msg.status);
                          return (
                            <div style={styles.senderHeader}>
                              <span>{formatTime(msg.timestamp || msg.created_at)}</span>
                              {msg.edited && !isDeleted && (
                                <span
                                  style={styles.messageEditedSent}
                                  title={msg.edit_history ? 'Editada pelo WhatsApp' : undefined}
                                >
                                  editada
                                </span>
                              )}
                              {isAgentMsg && <Bot size={11} style={{ flexShrink: 0 }} />}
                              {author?.kind === 'device' && <Smartphone size={11} style={{ flexShrink: 0 }} />}
                              {author?.label && (
                                <span title={author.title || undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                  {author.label}
                                </span>
                              )}
                              {/* Saiu por OUTRO número (origem ≠ a conexão deste
                                  workspace): confirma "· pelo nº de X". */}
                              {mirrorVia && (
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {`· pelo nº de ${mirrorVia.split(' ')[0]}`}
                                </span>
                              )}
                              {getMessageStatusIcon(msg.status)}
                              {st.text && <span style={{ color: st.color }}>{st.text}</span>}
                            </div>
                          );
                        })()}

                        {/* Apagada pelo cliente (revoke): só o aviso, sem
                            mídia, citação ou reação. */}
                        {isDeleted && (
                          <p style={styles.messageDeleted}>Mensagem apagada</p>
                        )}

                        {/* Citação ("responder em cima"): clicar rola até a
                            mensagem original. */}
                        {!isDeleted && msg.quoted && (
                          <QuotedMessage
                            quoted={msg.quoted}
                            contactName={chat?.contact_name}
                            colors={colors}
                            fromMe={isSent}
                            onJump={jumpToMessage}
                          />
                        )}

                        {/* Anúncio que trouxe o lead (INBOX_AD_CARD_ENABLED) */}
                        {!isDeleted && adContextOf(msg) && !isSent && (
                          <AdContextCard ad={adContextOf(msg)} colors={colors} />
                        )}

                        {/* Media Content */}
                        {!isDeleted && hasMediaContent(msg) && (
                          <div style={{ marginBottom: msg.text ? '8px' : '0' }}>
                            <WABusinessMessageMedia msg={msg} isSent={isSent} />
                          </div>
                        )}

                        {/* Tag visual indicando que e um template (HSM) — o
                            corpo do template aparece logo abaixo como texto
                            normal. Usamos verde primary com fundo translucido
                            para ter contraste em ambos os temas: balao de
                            enviado e verde claro no light e verde escuro no
                            dark — branco/preto puro nao funcionaria nos dois. */}
                        {!isDeleted && msg.type === 'template' && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: '6px',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: 600,
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            backgroundColor: `${colors.accentPrimary}22`,
                            color: colors.accentPrimary,
                          }}>
                            <FileText size={10} />
                            Template da Meta
                          </div>
                        )}

                        {/* Text Content (skip for image/video as the media component shows caption) */}
                        {!isDeleted && msg.text && !(hasMediaContent(msg) && (msg.type === 'image' || msg.type === 'video'))
                          && displayText(msg.text, { hasMedia: hasMediaContent(msg) }) && (
                          <p style={styles.messageText}>
                            <MessageText text={displayText(msg.text, { hasMedia: hasMediaContent(msg) })} />
                          </p>
                        )}
                        
                        {/* Failed message error — quando o erro for de
                            janela 24h, oferecemos um botao direto pra
                            enviar um template Meta (unica opcao valida
                            nesse cenario). Para outros erros mostramos
                            so o texto. */}
                        {isFailed && msg.error_message && (() => {
                          const is24hError =
                            /24h|janela|131047/i.test(msg.error_message || '');
                          return (
                            <div style={{ marginTop: '6px' }}>
                              <p style={{
                                fontSize: '11px',
                                color: colors.danger || '#ef4444',
                                fontStyle: 'italic',
                                margin: 0,
                              }}>
                                {msg.error_message}
                              </p>
                              {is24hError && (
                                <button
                                  onClick={() => {
                                    setSavedDrawerInitialTab('templates');
                                    setShowSavedDrawer(true);
                                  }}
                                  title="Abrir Templates da Meta e enviar um template aprovado"
                                  style={{
                                    marginTop: 6,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    padding: '4px 10px',
                                    borderRadius: 999,
                                    border: `1px solid ${colors.danger || '#ef4444'}55`,
                                    backgroundColor: 'transparent',
                                    color: colors.danger || '#ef4444',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <FileText size={11} />
                                  Enviar template
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Recebidas mantêm o horário discreto embaixo; as
                            enviadas levam tudo no cabeçalho (acima). */}
                        {!isSent && (
                          <div style={styles.messageMeta}>
                            <span style={styles.messageTimeReceived}>
                              {formatTime(msg.timestamp || msg.created_at)}
                            </span>
                            {msg.edited && !isDeleted && (
                              <span
                                style={styles.messageEdited}
                                title={msg.edit_history ? 'Editada pelo WhatsApp' : undefined}
                              >
                                editada
                              </span>
                            )}
                            {mirrorVia && (
                              <span style={styles.messageTimeReceived}>
                                {` · no nº de ${mirrorVia.split(' ')[0]}`}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Reações NA bolha da mensagem reagida, como no
                            WhatsApp: `reaction` = do cliente, `reaction_from_me`
                            = do operador (eco do app na coexistência). O
                            backend faz patch no doc alvo — reação nunca é
                            mensagem avulsa. */}
                        {!messageActionsOn && !isDeleted && (msg.reaction || msg.reaction_from_me) && (
                          <div style={{
                            marginTop: 3, textAlign: isSent ? 'left' : 'right',
                            fontSize: 13, lineHeight: 1,
                          }}>
                            {msg.reaction && <span>{msg.reaction}</span>}
                            {msg.reaction_from_me && (
                              <span style={{ marginLeft: msg.reaction ? 4 : 0 }}>
                                {msg.reaction_from_me}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Com a flag: chips de reação embaixo da bolha (do
                          contato e a sua; clicar na sua troca ou remove). */}
                      {messageActionsOn && !isDeleted && (msg.reaction || msg.reaction_from_me) && (
                        <div style={{ flexBasis: '100%' }}>
                          <ReactionChips
                            reaction={msg.reaction}
                            reactionFromMe={msg.reaction_from_me}
                            align={isSent ? 'right' : 'left'}
                            colors={colors}
                            onChangeMine={msg._isTemp ? undefined : (emoji) => react(msg, emoji)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll Button */}
      {showScrollButton && (
        <button style={styles.scrollButton} onClick={scrollToBottom}>
          <ArrowDown size={20} />
        </button>
      )}

      {/* Modal de Encaminhar (abre pelo ícone no header quando há mensagens
          selecionadas — a MESMA seleção do aiWorkspace) */}
      <ForwardModal
        isOpen={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        messageCount={aiWorkspace.selectedMessages.size}
        destinations={forwardDestinations}
        onForward={handleForwardSend}
        colors={colors}
      />

      {/* AI Workspace */}
      <AIWorkspace
        isOpen={aiWorkspace.isWorkspaceOpen}
        onToggleWorkspace={aiWorkspace.toggleWorkspace}
        selectedCount={aiWorkspace.selectedMessages.size}
        inputText={newMessage}
        
        // Dropdown
        activeDropdown={aiWorkspace.activeDropdown}
        onToggleDropdown={aiWorkspace.toggleDropdown}
        onCloseDropdown={aiWorkspace.closeDropdown}
        
        // Translate
        translateLanguages={aiWorkspace.translateLanguages}
        onToggleTranslateLanguage={aiWorkspace.toggleTranslateLanguage}
        translateResults={aiWorkspace.translateResults}
        translateProcessing={aiWorkspace.translateProcessing}
        translateError={aiWorkspace.translateError}
        hasTranslateResults={aiWorkspace.hasTranslateResults}
        
        // Generate
        generateResults={aiWorkspace.generateResults}
        generateProcessing={aiWorkspace.generateProcessing}
        generateError={aiWorkspace.generateError}
        hasGenerateResults={aiWorkspace.hasGenerateResults}
        
        // Custom Prompt
        customResults={aiWorkspace.customResults}
        customProcessing={aiWorkspace.customProcessing}
        customError={aiWorkspace.customError}
        hasCustomResults={aiWorkspace.hasCustomResults}
        
        // History
        historyItems={aiWorkspace.historyItems}
        isHistoryOpen={aiWorkspace.isHistoryOpen}
        onToggleHistory={aiWorkspace.toggleHistory}
        onRemoveFromHistory={aiWorkspace.removeFromHistory}
        onClearHistory={aiWorkspace.clearHistory}
        hasHistory={aiWorkspace.hasHistory}

        // Config das tags + handlers compartilhados com o menu de 3 pontinhos.
        workspaceConfig={workspaceConfigState.config}
        onOpenSavedMessages={() => {
          setSavedDrawerInitialTab('saved');
          setShowSavedDrawer(true);
        }}
        onOpenLabels={() => setShowLabelPicker(true)}
        onOpenFollowup={() => setShowScheduleModal(true)}
        onOpenFeedback={() => setShowFeedbackModal(true)}
        onOpenWorkspaceConfig={() => setShowWorkspaceConfig(true)}

        // Actions
        onClear={aiWorkspace.clearSelection}
        onExecuteTranslate={handleExecuteTranslate}
        onExecuteGenerate={handleExecuteGenerate}
        onExecuteCustom={handleExecuteCustom}
        onUseResponse={handleUseResponse}
        onRegenerateTranslate={handleRegenerateTranslate}
        onRegenerateGenerate={handleRegenerateGenerate}
        onRegenerateCustom={handleRegenerateCustom}
        onRemoveTranslateResult={aiWorkspace.removeTranslateResult}
        onRemoveGenerateResult={aiWorkspace.removeGenerateResult}
        onRemoveCustomResult={aiWorkspace.removeCustomResult}
        
        // Regeneration
        getTranslateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.TRANSLATE)}
        getGenerateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.GENERATE)}
        getCustomRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.CUSTOM)}
        regeneratingId={regeneratingId}

        // Context preview for "Chamar o Agente" modal
        selectedMessagesPreview={selectedMessagesPreview}

        // Roletas de transferencia (grupos do admin) — chips dinamicos
        transferRoulettes={transferRoulettes}
        onExecuteRoulette={handleExecuteRoulette}
        rouletteProcessingId={rouletteBusyId}

        // Funcionalidade B — número de ORIGEM. Só as origens deste canal
        // (wa_business) no inbox Business; o backend roteia o envio físico.
        originOptions={(origin.options || []).filter((o) => o.channel === 'wa_business')}
        activeOrigin={origin.activeOrigin?.channel === 'wa_business' ? origin.activeOrigin : null}
        onSelectOrigin={origin.setOrigin}
        allowSwitchOrigin={origin.canSwitch}
      />

      {/* 24h Window Error — fora da janela 24h, mensagem livre nao pode ser
          entregue. Oferecemos atalho para enviar um template Meta, que e a
          unica forma valida nesse cenario. */}
      {/* DIV-22 (2026-10-20): selo da janela de 24 h, aberta ou fechada, antes do
          erro acontecer (a guarda do backend recusa antes de chamar a Meta). */}
      {!windowError && janelaSelo && (
        <div style={{ padding: '4px 16px', fontSize: 11, color: janelaSelo.aberta ? colors.textSecondary : (colors.warning || '#f59e0b') }}>
          {janelaSelo.texto}
        </div>
      )}
      {windowError && (
        <div style={styles.windowError}>
          <AlertCircle size={20} color={colors.warning || '#f59e0b'} />
          <span style={styles.windowErrorText}>{windowError}</span>
          <button
            onClick={() => {
              setWindowError(null);
              setSavedDrawerInitialTab('templates');
              setShowSavedDrawer(true);
            }}
            title="Abrir Templates da Meta e enviar um template aprovado"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              border: 'none',
              backgroundColor: colors.accentPrimary,
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            <FileText size={12} />
            Enviar template
          </button>
          <button
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.textTertiary,
              cursor: 'pointer',
              padding: '4px',
            }}
            onClick={() => setWindowError(null)}
            title="Fechar aviso"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Barra de seleção (flag message_actions): substitui o composer
          enquanto há mensagens selecionadas, com Encaminhar à vista. */}
      {messageActionsOn && aiWorkspace.selectedMessages.size > 0 ? (
        <SelectionBar
          count={aiWorkspace.selectedMessages.size}
          max={MAX_SELECTED_MESSAGES}
          onForward={() => setShowForwardModal(true)}
          onAskAgent={() => aiWorkspace.openWorkspace()}
          onCancel={aiWorkspace.clearSelection}
          colors={colors}
        />
      ) : (
      <div style={styles.inputContainer}>
        {/* Upload Error Message */}
        {uploadError && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: `${colors.danger || '#ef4444'}15`,
            borderTop: `1px solid ${colors.danger || '#ef4444'}30`,
            color: colors.danger || '#ef4444',
            fontSize: '12px',
          }}>
            ⚠️ {uploadError}
          </div>
        )}
        
        {/* Responder citando: prévia da mensagem escolhida (Esc cancela) */}
        {messageActionsOn && replyTo && (
          <ReplyPreview
            mode="reply"
            quoted={quotedFromMessage(replyTo)}
            contactName={chat?.contact_name}
            colors={colors}
            onCancel={cancelReply}
          />
        )}

        {/* Attached Files Preview */}
        <AttachedFilesPreview
          files={attachedFiles}
          onRemoveFile={handleRemoveAttachedFile}
          onClearAll={handleClearAttachedFiles}
        />
        
        {/* Input Form — anexo e enviar circulares dentro do mesmo container,
            estilo do TextAudioInput do onboarding. */}
        <form onSubmit={handleSend} style={styles.inputForm}>
          <div style={styles.inputWrapper}>
            {audioRecorder.isRecording ? (
              <RecordingBar
                seconds={audioRecorder.seconds}
                sending={sendingAudio}
                onCancel={handleCancelRecording}
                onSend={handleStopAndSendAudio}
                colors={colors}
              />
            ) : (
              <>
                <AttachButton
                  channel="wa_business"
                  buttonStyle={styles.inputActionBtn}
                  onUploaded={handleFilesUploaded}
                  onError={handleUploadError}
                  onUploadingChange={setUploading}
                />
                <textarea
                  ref={attachInputRef}
                  placeholder="Digite uma mensagem"
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  onKeyDown={handleInputKeyDown}
                  rows={1}
                  disabled={sending}
                  style={styles.textarea}
                />
                {canSend ? (
                  <button
                    type="submit"
                    style={{
                      ...styles.sendBtn,
                      ...(sending ? styles.sendBtnDisabled : {}),
                    }}
                    disabled={sending}
                    title="Enviar mensagem"
                  >
                    {sending ? (
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartRecording}
                    style={{
                      ...styles.sendBtn,
                      ...(sending || sendingAudio || uploading ? styles.sendBtnDisabled : {}),
                    }}
                    disabled={sending || sendingAudio || uploading}
                    title="Gravar audio"
                  >
                    {sendingAudio ? (
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Mic size={18} />
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      </div>
      )}

      {/* 🆕 ADICIONAR: Modal de criar agente */}
      <CreateAgentModal />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;